import { COLORS, SPACING } from '@alto/design-library-tokens';
import { Column, DropShadow, LgPadding, Row, useScreenSize } from '@alto/design-system';
import React, { type ReactNode, useEffect } from 'react';
import { View } from 'react-native';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import styledNative from 'styled-components/native';
import { closeBanner } from '~shared/actions/banners';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { VERIFY } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import getAuth from '~shared/selectors/auth/getAuth';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { AuthorizedDashboardNavbar } from '~web/features/app/components/AuthorizedDashboardNavBar';
import { useIsHubReferralInfoRoute, useIsOnboardingRoute, useIsProgynyWizardRoute } from '~web/hooks';

// Fuze logo is shorter on authorized mobile so nav icons fit on one row (see LinkLogo).
// Vertical padding / logo / icon size changes must update AUTHORIZED_*_DASHBOARD_NAVBAR_HEIGHT_PX —
// the flow spacer and Cart both depend on them.
export const DASHBOARD_NAVBAR_LOGO_HEIGHT_PX = {
  UNAUTHORIZED_OR_DESKTOP: 32,
  AUTHORIZED_MOBILE: 24,
} as const;

/** NavBarWrapper LgPadding SM top+bottom + authorized mobile logo row. */
export const AUTHORIZED_MOBILE_DASHBOARD_NAVBAR_HEIGHT_PX = `${SPACING.STATIC.SM.value * 2 + DASHBOARD_NAVBAR_LOGO_HEIGHT_PX.AUTHORIZED_MOBILE}px`;

/** NavBarWrapper LgPadding SM top+bottom + authorized desktop logo row (matches WEB_NAVBAR_HEIGHT). */
export const AUTHORIZED_DESKTOP_DASHBOARD_NAVBAR_HEIGHT_PX = `${SPACING.STATIC.SM.value * 2 + DASHBOARD_NAVBAR_LOGO_HEIGHT_PX.UNAUTHORIZED_OR_DESKTOP}px`;

export type DashboardNavbarOffsetHeightPx = typeof AUTHORIZED_MOBILE_DASHBOARD_NAVBAR_HEIGHT_PX;

export const getDashboardNavbarOffsetPx = (isSMScreen: boolean): DashboardNavbarOffsetHeightPx =>
  isSMScreen ? AUTHORIZED_MOBILE_DASHBOARD_NAVBAR_HEIGHT_PX : AUTHORIZED_DESKTOP_DASHBOARD_NAVBAR_HEIGHT_PX;

const NavBarOffset = styledNative(View)<{ height: DashboardNavbarOffsetHeightPx }>`
  height: ${({ height }) => height};
  width: 100%;
`;

const DashboardNavbarOffset = () => {
  const { isSMScreen } = useScreenSize();

  return (
    <NavBarOffset
      testID="dashboard-navbar-offset"
      height={getDashboardNavbarOffsetPx(isSMScreen)}
    />
  );
};

const StyledLinkLogo = styled(NavLink)<{ isSMScreen: boolean; authorized: boolean }>`
  height: ${({ authorized, isSMScreen }) =>
    !isSMScreen || !authorized
      ? `${DASHBOARD_NAVBAR_LOGO_HEIGHT_PX.UNAUTHORIZED_OR_DESKTOP}px`
      : `${DASHBOARD_NAVBAR_LOGO_HEIGHT_PX.AUTHORIZED_MOBILE}px`};
  > img {
    height: inherit;
    width: inherit;
  }
`;

type LogoLinkProps = {
  readonly authorized: boolean;
  readonly to: string;
};

export const LinkLogo = ({ authorized, to }: LogoLinkProps) => {
  const { isSMScreen } = useScreenSize();
  const isAuthorizedMobile = isSMScreen && authorized;

  return (
    <StyledLinkLogo
      isSMScreen={isSMScreen}
      authorized={authorized}
      to={to}
    >
      <img
        src={getImage(isAuthorizedMobile ? 'logoMobile' : 'logo')}
        alt={IMG_ALT_TEXTS.logo}
      />
    </StyledLinkLogo>
  );
};

export const Navbar = styledNative(Column)<{ isOnboarding: boolean; authorized: boolean }>`
  top: 0;
  left: 0;
  right: 0;
  background: ${() => COLORS.BACKGROUND_COLORS.WHITE};
  z-index: 1;
  ${({ authorized }) =>
    authorized
      ? `
    z-index: 999;
  `
      : `
    z-index: 1000;
  `};
  position: ${({ isOnboarding }) => (isOnboarding ? 'absolute' : 'fixed')};
`;

type WrapperProps = {
  readonly showProgynyBackground: boolean;
  readonly authorized: boolean;
  readonly isOnboarding: boolean;
  readonly children: ReactNode;
};

export const getBackgroundColor = ({
  isOnboarding,
  showProgynyBackground,
}: {
  isOnboarding: boolean;
  showProgynyBackground: boolean;
}) => {
  if (showProgynyBackground) return COLORS.BACKGROUND_COLORS.PINK_LIGHTEST;
  if (isOnboarding) return COLORS.BACKGROUND_COLORS.GREY_LIGHTEST;

  return COLORS.BACKGROUND_COLORS.WHITE;
};

// LgPadding SM top+bottom — keep AUTHORIZED_*_DASHBOARD_NAVBAR_HEIGHT_PX in sync when padding changes.
export const NavBarWrapper = ({ children, showProgynyBackground, authorized, isOnboarding }: WrapperProps) => {
  return (
    <Navbar
      authorized={authorized}
      backgroundColor={getBackgroundColor({ isOnboarding, showProgynyBackground })}
      isOnboarding={isOnboarding}
    >
      <DropShadow bottomDark>
        <LgPadding
          topPadding={SPACING.STATIC.SM}
          bottomPadding={SPACING.STATIC.SM}
        >
          <div className="branch-journeys-top" />
          {children}
        </LgPadding>
      </DropShadow>
    </Navbar>
  );
};

export const DashboardNavbar = () => {
  const dispatch = useDispatchShared();
  const location = useLocation();
  const pathname = location.pathname;
  const { authorized, onboarding: isLegacyOnboarding } = useSelectorShared(getAuth);
  const showBanner = useSelectorShared((state) => state.banners.showBanner);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const { isHubReferralInfoRoute } = useIsHubReferralInfoRoute();
  const { isProgynyWizardRoute } = useIsProgynyWizardRoute();
  const { isOnboardingRoute, isOrganicOnboardingRoute } = useIsOnboardingRoute();
  const onboarding = isLegacyOnboarding || isOnboardingRoute || isOrganicOnboardingRoute;
  const currentRoute = routeFromPath(pathname);
  const brandLink = authorized ? '/home' : '/';
  const showProgynyBackground = showProgyny && onboarding;
  const isOnboardingVerify = currentRoute === VERIFY;
  const isOnboarding = onboarding && !isOnboardingVerify;
  const inCheckout = pathname.match(/checkout/g);

  useEffect(() => {
    if (inCheckout && showBanner) {
      dispatch(closeBanner);
    }
  }, [dispatch, inCheckout, showBanner]);

  if (onboarding || isHubReferralInfoRoute || isProgynyWizardRoute) {
    return null;
  }

  if (authorized) {
    return (
      <>
        <AuthorizedDashboardNavbar
          authorized={authorized}
          closeBanner={closeBanner}
          showBanner={showBanner}
          showProgyny={showProgyny}
          onboarding={onboarding}
        />
        <DashboardNavbarOffset />
      </>
    );
  }

  return (
    <NavBarWrapper
      showProgynyBackground={showProgynyBackground}
      authorized={authorized}
      isOnboarding={isOnboarding}
    >
      <Row center>
        <LinkLogo
          to={brandLink}
          authorized={authorized}
        />
      </Row>
    </NavBarWrapper>
  );
};
